/**
 * The Treatment Skin Boutique – Price List 2025
 * Sourced from PRICE LIST 2025.pdf. Use for display and treatment meta price ranges.
 */

import {
  getWellnestOfferingByTreatmentName,
  getWellnestPeptideMeta,
  isWellnestWellnessProviderCode,
  parseWellnestPriceMin,
  WELLNEST_OFFERINGS,
} from "./wellnestOfferings";
import {
  getJudgeMdFacialPlanSubcategoryForItemName,
  getJudgeMdProductOptionsForSurgeryCategory,
  isJudgeMdFacialPlanSubcategory,
  isJudgeMdProviderCode,
  isJudgeMdSurgeryPlanCategory,
  JUDGEMD_FACIAL_SURGERY_PLAN_CATEGORIES,
  JUDGEMD_PRICE_LIST_2026_27,
  JUDGEMD_SURGERY_SECTION_CATEGORIES,
} from "./judgeMdPricing2026";
import {
  GRAVITAS_PRICE_LIST_2026,
  isGravitasProviderCode,
} from "./gravitasPricing2026";
export interface TreatmentPriceItem {
  name: string;
  price: number;
  note?: string;
}

/** Category used in the dashboard (maps to ALL_TREATMENTS). */
export type DashboardTreatmentCategory =
  | "Skincare"
  | "Energy Treatment"
  | "Facial Services"
  | "Chemical Peel"
  | "Microneedling"
  | "Filler"
  | "Neurotoxin"
  | "Biostimulants"
  | "Other procedures"
  /** JudgeMD embedded list — one plan category per surgery section (facial split into subs on plan builder). */
  | "Breast Surgery"
  | "Facial Surgery"
  | "Rhinoplasty"
  | "Facial Surgery — Lifting & threads"
  | "Facial Surgery — Eyes & brows"
  | "Facial Surgery — Rhinoplasty"
  | "Facial Surgery — Lips, chin & jaw"
  | "Facial Surgery — Fat transfer"
  | "Facial Surgery — Ears"
  | "Facial Surgery — Forehead, hairline & skin"
  | "Body Sculpting"
  | "Vaginal Rejuvenation"
  | "Kybella"
  | "Threadlift";

/** All 2025 prices by PDF section (for reference and lookup). */
export const TREATMENT_PRICE_LIST_2025: {
  category: string;
  items: TreatmentPriceItem[];
}[] = [
  {
    category: "Consultations",
    items: [
      { name: "Acne Consultation", price: 150 },
      { name: "Cosmelan Consultation", price: 150 },
      { name: "Injectable Consultation", price: 150 },
      { name: "Laser Consultation", price: 150 },
      { name: "Ultherapy Consultation", price: 150 },
      { name: "Hair Restoration Consultation", price: 150 },
    ],
  },
  {
    category: "Injectables",
    items: [
      { name: "Botox 1-Unit", price: 13, note: "CA locations only" },
      { name: "Dysport 1-Unit", price: 5.2, note: "CA locations only" },
      { name: "Botox Sweating Treatment", price: 995 },
      { name: "Botox – Henderson (1–50u vial)", price: 650 },
      { name: "Dysport – Henderson (1–300u vial)", price: 995 },
      { name: "Fillers (except Voluma & Volux)", price: 750 },
      { name: "Voluma", price: 850, note: "per syringe" },
      { name: "Volux", price: 950 },
      { name: "Radiesse", price: 900 },
      { name: "Radiesse – 2 Syringes", price: 1530 },
      { name: "Radiesse – 3 Syringes", price: 2295 },
      { name: "Radiesse – 4 Syringes", price: 2880 },
      { name: "Radiesse – 5 Syringes", price: 3510 },
      { name: "Radiesse – 6 Syringes", price: 4050 },
      { name: "Sculptra – 1 Vial", price: 800 },
      { name: "Sculptra – 3 Vials", price: 2250 },
      { name: "Sculptra – 4 Vials", price: 3000 },
      { name: "Sculptra – 5 Vials", price: 3500 },
      { name: "Sculptra – 6 Vials", price: 4000 },
      { name: "Sculptra – 8 Vials", price: 5200 },
      { name: "Skinvive", price: 750, note: "2 syringes" },
      {
        name: "Skinvive Add-On Syringe",
        price: 375,
        note: "per syringe (3rd and beyond)",
      },
      { name: "Spider Vein Treatment", price: 750 },
      { name: "Spider Vein Treatment Package (3)", price: 2025 },
    ],
  },
  {
    category: "Other Treatments",
    items: [
      { name: "Filler Dissolver", price: 300 },
      { name: "Pronox Treatment", price: 75 },
      { name: "Vitamin B-12 Shot", price: 25 },
      { name: "Cortisone Shot", price: 50 },
      { name: "Zapping Treatment (Milia/Sebaceous Hyperplasia)", price: 150 },
      { name: "Light Stim Add-On", price: 40, note: "Esti services only" },
      { name: "Nerve Block", price: 25 },
    ],
  },
  {
    category: "Facial Services",
    items: [
      { name: "Focused Facial", price: 75 },
      { name: "Acne Facial", price: 150 },
      { name: "Calming Facial", price: 150 },
      { name: "Signature Facial", price: 150 },
      { name: "Treatman Facial", price: 150 },
      { name: "Treat Yourself Facial", price: 200 },
      { name: "Glass Skin Facial", price: 250 },
      { name: "Dermaplaning", price: 150 },
      { name: "Dermasweep", price: 150 },
      { name: "Dermasweep – Face, Neck & Chest", price: 275 },
      { name: "Dermasweep w/ Premium Infusion", price: 225 },
      { name: "Dermasweep w/ Premium Infusion – Face, Neck & Chest", price: 325 },
    ],
  },
  {
    category: "Chemical Peel",
    items: [
      { name: "Brightening Lactic Peel – Full Face", price: 85 },
      { name: "Brightening Lactic Peel – Full Back", price: 225 },
      { name: "Brightening Lactic Peel – Upper Back", price: 165 },
      { name: "Jessner's Peel – Full Face", price: 180 },
      { name: "Jessner's Peel – Face & Neck or Chest", price: 270 },
      { name: "Jessner's Peel – Face, Neck & Chest", price: 360 },
      { name: "Jessner's Peel – Full Back", price: 415 },
      { name: "Jessner's Peel – Upper Back", price: 355 },
      { name: "Sal-X Plus Acne Peel – Full Face", price: 125 },
      { name: "Sal-X Plus Acne Peel – Face, Neck & Chest", price: 250 },
      { name: "Sal-X Plus Acne Peel – Face, Neck or Chest", price: 180 },
      { name: "Sal-X Plus Acne Peel – Full Back", price: 310 },
      { name: "Sal-X Plus Acne Peel – Upper Back", price: 245 },
      { name: "Depigmentation peel", price: 900 },
      { name: "Lactic Peel Add-On", price: 50, note: "Esti services only" },
    ],
  },
  {
    category: "Sofwave",
    items: [
      { name: "Sofwave – Full Face", price: 2850, note: "Claremont only" },
      { name: "Sofwave – Full Face + Neck", price: 3900 },
      { name: "Sofwave – Lower Face", price: 2000 },
      { name: "Sofwave – Lower Face + Neck", price: 3150 },
      { name: "Sofwave – Neck", price: 1650 },
      { name: "Sofwave – Brows", price: 800 },
    ],
  },
  {
    category: "Ultherapy",
    items: [
      { name: "Ultherapy – Full Face", price: 3500, note: "Newport only" },
      { name: "Ultherapy – Full Face + Neck", price: 4000 },
      { name: "Ultherapy – Lower Face", price: 2500 },
      { name: "Ultherapy – Lower Face + Neck", price: 3500 },
      { name: "Ultherapy – Upper Face", price: 1500 },
      { name: "Ultherapy – Neck", price: 2000 },
      { name: "Ultherapy – Brows", price: 500 },
    ],
  },
  {
    category: "Laser",
    items: [
      { name: "Moxi Full Face", price: 550 },
      { name: "Moxi Face, Neck & Chest", price: 995 },
      { name: "Moxi Neck", price: 400 },
      { name: "Moxi Chest", price: 550 },
      { name: "Moxi Hands", price: 350 },
      { name: "BBL Full Face", price: 550 },
      { name: "BBL Face, Neck & Chest", price: 995 },
      { name: "BBL Neck", price: 400 },
      { name: "BBL Chest", price: 550 },
      { name: "BBL Hands", price: 350 },
      { name: "BBL Full Arms & Hands", price: 995 },
      { name: "BBL Upper Arms", price: 600 },
      { name: "BBL Forearms", price: 600 },
      { name: "BBL Full Back", price: 850 },
      { name: "BBL Upper Back", price: 550 },
      { name: "BBL Lower Back", price: 550 },
      { name: "BBL Full Legs", price: 1075 },
      { name: "BBL Upper Legs/Thighs", price: 850 },
      { name: "BBL Lower Legs", price: 650 },
      { name: "BBL Spot Treatment", price: 250 },
      { name: "BBL + Moxi Full Face", price: 750 },
      { name: "BBL + Moxi Face, Neck & Chest", price: 1150 },
      { name: "BBL + Moxi Neck", price: 500 },
      { name: "BBL + Moxi Chest", price: 650 },
      { name: "BBL + Moxi Arms & Hands", price: 1095 },
      { name: "BBL + Moxi Hands", price: 450 },
      { name: "Moxi Full Face 3PK", price: 1485 },
      { name: "Moxi Face, Neck & Chest 3PK", price: 2687 },
      { name: "BBL Full Face 3PK", price: 1485 },
      { name: "BBL + Moxi Full Face 3PK", price: 2025 },
      { name: "BBL + Moxi Face, Neck & Chest 3PK", price: 3105 },
    ],
  },
  {
    category: "Medical Spa",
    items: [
      { name: "Microneedling", price: 500, note: "CA locations only" },
      { name: "PDGF", price: 495, note: "CA locations only" },
      { name: "Microneedling & PRFM Add-On Neck or Chest", price: 250 },
      { name: "PRFM Microneedling", price: 775, note: "CA locations only" },
      { name: "PRFM Hair Restoration", price: 750, note: "CA locations only" },
      { name: "PRFM Injections", price: 750, note: "CA locations only" },
      { name: "PRFM Additional Tube", price: 250 },
      { name: "Microneedling (Henderson)", price: 300 },
      { name: "PRFM Microneedling (Henderson)", price: 550 },
      { name: "PRFM Hair Restoration (Henderson)", price: 500 },
      { name: "PRFM Injections (Henderson)", price: 500 },
      { name: "Microneedling 5PK", price: 2000, note: "CA" },
      { name: "Microneedling 5PK (Henderson)", price: 1200 },
      { name: "PRFM Microneedling 5PK", price: 3100, note: "CA" },
      { name: "PRFM Hair Restoration 5PK", price: 3000, note: "CA locations only" },
      { name: "PRFM Microneedling 5PK (Henderson)", price: 2200 },
      { name: "PRFM Hair Restoration 5PK (Henderson)", price: 2000 },
      { name: "PRFM Injections 5PK (Henderson)", price: 2000 },
    ],
  },
];

/** Add-on options from the 2025 price list (for checkout "Add-ons" section). */
export function getAddOnOptions(): TreatmentPriceItem[] {
  const out: TreatmentPriceItem[] = [];
  for (const section of TREATMENT_PRICE_LIST_2025) {
    for (const item of section.items) {
      if (/add-?on/i.test(item.name)) out.push(item);
    }
  }
  return out;
}

/** Format price for display. */
export function formatPrice(price: number): string {
  if (price % 1 === 0) return `$${price.toLocaleString()}`;
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
}

/** Get min and max price from a list of items. */
function getMinMax(items: TreatmentPriceItem[]): { min: number; max: number } {
  if (items.length === 0) return { min: 0, max: 0 };
  const prices = items.map((i) => i.price);
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/**
 * Price-list labels merged into the by-treatment recommender search haystack so queries like
 * Legacy labels like "Skinvive II", "Cortisone", "Pronox", or "Cosmelan" surface the right category card even when
 * those strings are not in {@link TREATMENT_PRODUCT_OPTIONS} (e.g. old sheet names vs "Skinvive (skin booster)").
 */
export function getPriceListLabelsForTreatmentRecommenderSearch(
  treatment: string,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): string {
  const dash = normalizePlanTreatmentCategoryForPricing(treatment.trim());
  if (!dash || !(dash in DASHBOARD_TO_PRICE_SECTIONS)) return "";

  const seen = new Set<string>();
  const parts: string[] = [];
  const add = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    const k = t.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    parts.push(t);
  };

  const items = getItemsForDashboardCategory(dash as DashboardTreatmentCategory, priceList);
  for (const i of items) add(i.name);

  if (dash === "Chemical Peel") {
    add("Cosmelan");
    add("Cosmelan MD");
  }

  if (dash === "Other procedures") {
    const otherTreat = priceList.find((s) => s.category === "Other Treatments");
    if (otherTreat) {
      for (const i of otherTreat.items) add(i.name);
    }
    const inj = priceList.find((s) => s.category === "Injectables");
    if (inj) {
      for (const i of inj.items) {
        if (/spider\s*vein/i.test(i.name)) add(i.name);
      }
    }
  }

  return parts.join(" ");
}

const JUDGEMD_SURGERY_CAT_SET = new Set<string>(JUDGEMD_SURGERY_SECTION_CATEGORIES);

/** Injectables row → Neurotoxin plan category (2025 sheet + JudgeMD-style names). */
function injectableMatchesNeurotoxinSku(i: TreatmentPriceItem): boolean {
  const n = i.name;
  return (
    n.includes("Botox") ||
    n.includes("Dysport") ||
    /daxxify|doxify/i.test(n) ||
    n.includes("Sweating") ||
    /masseter|trapezius|lip flip|baby tox|gummy smile|browlift to neuro/i.test(n)
  );
}

/** Injectables row → Filler plan category. */
function injectableMatchesFillerSku(i: TreatmentPriceItem): boolean {
  const n = i.name;
  return (
    n.includes("Filler") ||
    n.includes("Voluma") ||
    n.includes("Volux") ||
    n.includes("Dissolver") ||
    /juvederm|restylane|revance|liquid rhinoplasty|skinvive|redensity|rha\d|rha\s|renuva/i.test(
      n,
    )
  );
}

/** Injectables row → Biostimulants plan category. Skinvive (intradermal) maps as filler only. */
function injectableMatchesBiostimulantSku(i: TreatmentPriceItem): boolean {
  const n = i.name;
  if (/skinvive/i.test(n)) return false;
  /** JudgeMD sheet: Renuva (3 cc) is under the Filler plan card, not Biostimulants. */
  if (/renuva/i.test(n) && /\(3\s*cc\)/i.test(n)) return false;
  return (
    n.includes("Radiesse") ||
    n.includes("Sculptra") ||
    /ez\s*gel/i.test(n)
  );
}

/** All items that map to a dashboard category (for price range). */
function getItemsForDashboardCategory(
  category: DashboardTreatmentCategory,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): TreatmentPriceItem[] {
  const flat: TreatmentPriceItem[] = [];
  for (const section of priceList) {
    if (
      section.category === "Injectables" &&
      (category === "Filler" || category === "Neurotoxin" || category === "Biostimulants")
    ) {
      const injectables = section.items;
      if (category === "Neurotoxin") {
        flat.push(...injectables.filter(injectableMatchesNeurotoxinSku));
      } else if (category === "Filler") {
        flat.push(...injectables.filter(injectableMatchesFillerSku));
      } else if (category === "Biostimulants") {
        flat.push(...injectables.filter(injectableMatchesBiostimulantSku));
      }
    } else if (section.category === "Facial Surgery") {
      if (isJudgeMdFacialPlanSubcategory(category)) {
        const allowed = new Set(getJudgeMdProductOptionsForSurgeryCategory(category));
        flat.push(...section.items.filter((i) => allowed.has(i.name)));
      } else if (category === "Facial Surgery") {
        flat.push(...section.items);
      } else if (category === "Other procedures") {
        flat.push(...section.items);
      }
    } else if (
      section.category === "Breast Surgery" ||
      section.category === "Body Sculpting" ||
      section.category === "Vaginal Rejuvenation"
    ) {
      if (category === section.category) {
        flat.push(...section.items);
      } else if (category === "Other procedures") {
        flat.push(...section.items);
      }
    } else if (
      section.category === "Chemical Peel" &&
      category === "Chemical Peel"
    ) {
      flat.push(...section.items);
    } else if (section.category === "Laser" && category === "Energy Treatment") {
      flat.push(...section.items);
    } else if (section.category === "Sofwave" && category === "Energy Treatment") {
      flat.push(...section.items);
    } else if (section.category === "Ultherapy" && category === "Energy Treatment") {
      flat.push(...section.items);
    } else if (
      section.category === "Medical Spa" &&
      category === "Microneedling"
    ) {
      flat.push(
        ...section.items.filter((i) => {
          const n = i.name;
          if (/PRFM\s*Injections/i.test(n) || /PRFM\s*Hair\s*Restoration/i.test(n)) {
            return false;
          }
          return n.includes("Microneedling") || n.includes("PRFM") || n.trim() === "PDGF";
        }),
      );
    } else if (
      section.category === "Medical Spa" &&
      category === "Other procedures"
    ) {
      flat.push(
        ...section.items.filter(
          (i) =>
            /PRFM\s*Injections/i.test(i.name) || /PRFM\s*Hair\s*Restoration/i.test(i.name),
        ),
      );
    } else if (
      section.category === "Injectables" &&
      category === "Other procedures"
    ) {
      flat.push(
        ...section.items.filter(
          (i) => i.name.includes("Skinvive") || /spider\s*vein/i.test(i.name),
        ),
      );
    } else if (
      section.category === "Other Treatments" &&
      category === "Other procedures"
    ) {
      flat.push(
        ...section.items.filter((i) => !/dissolver/i.test(i.name)),
      );
    }
  }
  return flat;
}

/**
 * Price range string for a dashboard treatment category from the given price list
 * (defaults to the 2025 sheet). Use with {@link getEffectivePriceList} for JudgeMD / custom sheets.
 * Returns undefined if no items map to that category.
 */
export function getPriceRange2025(
  category: DashboardTreatmentCategory,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): string | undefined {
  const items = getItemsForDashboardCategory(category, priceList);
  if (items.length === 0) return undefined;
  const { min, max } = getMinMax(items);
  if (min === max) return formatPrice(min);
  return `${formatPrice(min)}–${formatPrice(max)}`;
}

/**
 * Which unified dashboard treatment category (treatment recommender / plan) a price-list row
 * belongs to. Mirrors {@link getSkusForDashboardCategory} classification. Empty when the line
 * is reference-only (e.g. consultations) or not mapped to a plan category.
 */
export function getDashboardCategoriesForPriceListItem(
  sheetSectionCategory: string,
  itemName: string,
): DashboardTreatmentCategory[] {
  const sec = (sheetSectionCategory ?? "").trim();
  const n = itemName ?? "";
  if (sec === "Facial Services") {
    return ["Facial Services"];
  }
  if (sec === "Chemical Peel") return ["Chemical Peel"];
  if (sec === "Sofwave" || sec === "Ultherapy" || sec === "Laser") {
    return ["Energy Treatment"];
  }
  if (sec === "Medical Spa") {
    if (/PRFM\s*Injections/i.test(n) || /PRFM\s*Hair\s*Restoration/i.test(n)) {
      return ["Other procedures"];
    }
    if (n.includes("Microneedling") || n.includes("PRFM") || n.trim() === "PDGF") {
      return ["Microneedling"];
    }
    return [];
  }
  if (sec === "Injectables") {
    const sku = { name: n, price: 0 };
    if (injectableMatchesNeurotoxinSku(sku)) {
      return ["Neurotoxin"];
    }
    if (injectableMatchesFillerSku(sku)) {
      return ["Filler"];
    }
    if (injectableMatchesBiostimulantSku(sku)) {
      return ["Biostimulants"];
    }
    if (n.includes("Skinvive")) return ["Other procedures"];
    if (/spider\s*vein/i.test(n)) return ["Other procedures"];
    return [];
  }
  if (sec === "Other Treatments") {
    if (n.includes("Dissolver")) return ["Filler"];
    return ["Other procedures"];
  }
  if (sec === "Facial Surgery") {
    const sub = getJudgeMdFacialPlanSubcategoryForItemName(n);
    if (sub) return [sub as DashboardTreatmentCategory];
    return ["Facial Surgery"];
  }
  if (sec === "Breast Surgery" || sec === "Body Sculpting" || sec === "Vaginal Rejuvenation") {
    return [sec as DashboardTreatmentCategory];
  }
  return [];
}

/**
 * Line items from the 2025 price sheet for a dashboard treatment category (read-only reference UI).
 */
export function getPricingSheetLineItemsForTreatmentCategory(
  treatmentCategory: string,
): TreatmentPriceItem[] {
  const key = normalizePlanTreatmentCategoryForPricing(treatmentCategory);
  if (!key) return [];
  return getItemsForDashboardCategory(key as DashboardTreatmentCategory);
}

/**
 * Full flat list of all 2025 services with price and category (for search/display).
 */
export function getAllPrices2025(): (TreatmentPriceItem & { category: string })[] {
  const out: (TreatmentPriceItem & { category: string })[] = [];
  for (const section of TREATMENT_PRICE_LIST_2025) {
    for (const item of section.items) {
      out.push({ ...item, category: section.category });
    }
  }
  return out;
}

/** Numeric min/max for a dashboard category (for checkout totals). Kybella/Threadlift use fixed ranges; others from the given price list (defaults to 2025). */
export function getPriceRangeNumeric2025(
  category: string,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): { min: number; max: number } | undefined {
  const c = normalizePlanTreatmentCategoryForPricing((category ?? "").trim());
  if (!c) return undefined;
  // Categories not in TREATMENT_PRICE_LIST_2025 (from TREATMENT_META)
  const fallbacks: Record<string, { min: number; max: number }> = {
    Kybella: { min: 1200, max: 1800 },
    Threadlift: { min: 1500, max: 4000 },
  };
  if (fallbacks[c]) return fallbacks[c];
  const dash = c as DashboardTreatmentCategory;
  const items = getItemsForDashboardCategory(dash, priceList);
  if (items.length === 0) return undefined;
  return getMinMax(items);
}

/**
 * One line item for checkout: estimated price range for a plan item (by treatment category).
 * Used by TreatmentPlanCheckout to show per-line and total.
 */
export interface CheckoutLineItem {
  /** Display label (e.g. "Laser", "Neurotoxin • Forehead") */
  label: string;
  /** Estimated price min (0 if unknown) */
  min: number;
  /** Estimated price max (0 if unknown) */
  max: number;
  /** Formatted range string for display */
  displayPrice: string;
}

/**
 * Plan item shape used for price lookup (avoids coupling to DiscussedItem).
 */
export interface PlanItemForPricing {
  treatment: string;
  product?: string;
  region?: string;
  quantity?: string;
  /** See DiscussedItem: visits × vials-per-visit for Sculptra when set with quantity. */
  bioTreatmentSessions?: string;
}

/** Total Sculptra vials for SKU lookup (legacy: quantity alone = total vials when sessions unset). */
export function getSculptraVialCountForPlanPricing(item: PlanItemForPricing): number {
  const sessionsRaw = (item.bioTreatmentSessions ?? "").trim();
  const perVisitRaw = (item.quantity ?? "").trim();
  const sessions = sessionsRaw ? parseInt(sessionsRaw, 10) : NaN;
  const perVisit = perVisitRaw ? parseInt(perVisitRaw, 10) : NaN;
  if (
    !Number.isNaN(sessions) &&
    !Number.isNaN(perVisit) &&
    sessions > 0 &&
    perVisit > 0
  ) {
    return sessions * perVisit;
  }
  const legacy = perVisitRaw ? parseInt(perVisitRaw, 10) : NaN;
  if (!Number.isNaN(legacy) && legacy > 0) return legacy;
  return 1;
}

/** Category-level meta for checkout (longevity, recovery, sessions). Mirrors TREATMENT_META where used. */
export interface CategoryMeta {
  longevity?: string;
  downtime?: string;
  sessions?: string;
}

/** Provider code that must only see treatment options present in the 2025 price list. */
export const PROVIDER_CODE_RESTRICTED_TO_PRICE_LIST = "TheTreatment250";

/** Dashboard treatment category → price list section names (for SKU lookup). */
const DASHBOARD_TO_PRICE_SECTIONS: Record<string, string[]> = {
  Skincare: [],
  "Energy Treatment": ["Laser", "Sofwave", "Ultherapy"],
  "Facial Services": ["Facial Services"],
  "Chemical Peel": ["Chemical Peel"],
  Microneedling: ["Medical Spa"],
  Filler: ["Injectables"],
  Neurotoxin: ["Injectables"],
  Biostimulants: ["Injectables"],
  "Other procedures": ["Medical Spa", "Injectables", "Other Treatments"],
  "Breast Surgery": ["Breast Surgery"],
  "Facial Surgery": ["Facial Surgery"],
  ...Object.fromEntries(
    JUDGEMD_FACIAL_SURGERY_PLAN_CATEGORIES.filter((c) => c !== "Facial Surgery").map(
      (c) => [c, ["Facial Surgery"]],
    ),
  ),
  "Body Sculpting": ["Body Sculpting"],
  "Vaginal Rejuvenation": ["Vaginal Rejuvenation"],
  Kybella: [],
  Threadlift: [],
  /** Gravitas Medspa — IV drips and wellness injections */
  "IV & Wellness": ["IV & Wellness"],
};

/** Treatment categories that have at least one price list section (for provider-restricted views). */
export const TREATMENT_CATEGORIES_IN_PRICE_LIST: DashboardTreatmentCategory[] = (
  Object.entries(DASHBOARD_TO_PRICE_SECTIONS) as [DashboardTreatmentCategory, string[]][]
)
  .filter(([, sections]) => sections.length > 0)
  .map(([cat]) => cat);

/**
 * Map plan row `treatment` to keys in {@link DASHBOARD_TO_PRICE_SECTIONS}.
 * Some data sources use "Laser" (photos / legacy) or different letter case; without this,
 * SKU lookup returns nothing and quotes show $0 / "Price varies" (e.g. share treatment plan).
 */
export function normalizePlanTreatmentCategoryForPricing(treatment: string): string {
  const t = (treatment ?? "").trim();
  if (!t) return t;
  if (DASHBOARD_TO_PRICE_SECTIONS[t]) return t;
  const lower = t.toLowerCase().replace(/\s+/g, " ");
  const keys = Object.keys(DASHBOARD_TO_PRICE_SECTIONS) as string[];
  const caseMatch = keys.find((k) => k.toLowerCase() === lower);
  if (caseMatch) return caseMatch;
  if (lower === "laser" || lower === "energy device") return "Energy Treatment";
  if (lower === "other procedures" || lower === "other procedure") return "Other procedures";
  /** Legacy top-level rows; 2025 sheet lists PRFM under Medical Spa / microneedling family. */
  if (lower === "prp" || lower === "pdgf") return "Microneedling";
  return t;
}

/** True when the provider should only see options that exist in the 2025 pricing sheet. */
export function isProviderRestrictedToPricingSheet(providerCode: string | undefined): boolean {
  return (
    (providerCode ?? "").trim().toLowerCase() ===
      PROVIDER_CODE_RESTRICTED_TO_PRICE_LIST.toLowerCase() ||
    isGravitasProviderCode(providerCode)
  );
}

/** Energy modality type names from the 2025 price list (Laser, Sofwave, Ultherapy sections). Used as Type options for Energy Treatment category. */
export function getEnergyDeviceTypesFromPriceList(): string[] {
  const devices: string[] = [];
  for (const section of TREATMENT_PRICE_LIST_2025) {
    if (section.category === "Sofwave") {
      devices.push("Sofwave");
    } else if (section.category === "Ultherapy") {
      devices.push("Ultherapy");
    } else if (section.category === "Laser") {
      const seen = new Set<string>();
      for (const item of section.items) {
        const n = item.name;
        if (n.startsWith("Moxi") && !n.includes("BBL")) seen.add("Moxi");
        else if (n.startsWith("BBL + Moxi") || n.includes("BBL + Moxi")) {
          seen.add("Moxi + BBL");
          seen.add("BBL (BroadBand Light)");
        } else if (n.startsWith("BBL")) seen.add("BBL (BroadBand Light)");
      }
      if (seen.has("Moxi")) devices.push("Moxi");
      if (seen.has("BBL (BroadBand Light)")) devices.push("BBL (BroadBand Light)");
      if (seen.has("Moxi + BBL")) devices.push("Moxi + BBL");
    }
  }
  return [...new Set(devices)];
}

/** Energy treatment area names from the Laser / Sofwave / Ultherapy price list. */
export function getEnergyTreatmentAreasFromPriceList(): string[] {
  const areas: string[] = [];
  const pushArea = (area: string | null | undefined) => {
    const normalized = (area ?? "")
      .replace(/\s+\d+\s*PK$/i, "")
      .replace(/\s+/g, " ")
      .trim();
    if (normalized && !areas.includes(normalized)) areas.push(normalized);
  };

  for (const section of TREATMENT_PRICE_LIST_2025) {
    if (section.category === "Laser") {
      for (const item of section.items) {
        const area = item.name
          .replace(/^(BBL\s*\+\s*Moxi|Moxi|BBL)\s+/i, "")
          .trim();
        pushArea(area);
      }
    } else if (section.category === "Sofwave" || section.category === "Ultherapy") {
      for (const item of section.items) {
        const parts = item.name.split("–");
        pushArea(parts.length > 1 ? parts.slice(1).join("–") : null);
      }
    }
  }

  return areas;
}

function splitChemicalPeelTypeAndArea(name: string): { type: string; area: string | null } {
  const raw = (name ?? "").trim();
  if (!raw) return { type: "", area: null };
  const parts = raw.split(" – ");
  if (parts.length < 2) return { type: raw, area: null };
  return { type: parts[0]!.trim(), area: parts.slice(1).join(" – ").trim() || null };
}

/** Chemical peel SKU names from the 2025 price list. */
export function getChemicalPeelSkusFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Chemical Peel");
  if (!section) return [];
  return section.items.map((i) => i.name);
}

/** Facial service names from the 2025 price list. */
export function getFacialServiceTypesFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Facial Services");
  if (!section) return [];
  return section.items.map((i) => i.name);
}

/** Chemical peel type names from the 2025 price list (without area suffix). */
export function getChemicalPeelTypesFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Chemical Peel");
  if (!section) return [];
  const types = section.items.map((i) => splitChemicalPeelTypeAndArea(i.name).type).filter(Boolean);
  return Array.from(new Set(types));
}

/**
 * Normalize treatment area text so variants like "Face, Neck & Chest" / "Face & Neck or Chest"
 * / "Full Face + Neck" collapse to one comparable key (PDF uses inconsistent punctuation).
 */
function normalizeTreatmentAreaKey(area: string): string {
  return area
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u2019']/g, "")
    .replace(/,/g, " ")
    .replace(/\s*&\s*/g, " and ")
    .replace(/\s*\+\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function treatmentAreaMatchesSkuArea(
  skuArea: string | null | undefined,
  selectedArea: string,
): boolean {
  if (!skuArea?.trim() || !selectedArea.trim()) return false;
  return normalizeTreatmentAreaKey(skuArea) === normalizeTreatmentAreaKey(selectedArea);
}

function getLaserDevicePrefixFromProduct(product: string): string | null {
  const p = product.trim();
  if (/moxi\s*\+\s*bbl|bbl\s*\+\s*moxi/i.test(p)) return "BBL + Moxi";
  if (/^bbl/i.test(p)) return "BBL";
  if (/^moxi/i.test(p)) return "Moxi";
  return null;
}

function getLaserPrefixFromSkuName(skuName: string): string | null {
  if (/^BBL\s*\+\s*Moxi/i.test(skuName)) return "BBL + Moxi";
  if (/^Moxi/i.test(skuName)) return "Moxi";
  if (/^BBL/i.test(skuName)) return "BBL";
  return null;
}

function extractLaserAreaFromSkuName(skuName: string): string | null {
  const m = skuName.match(/^(?:BBL\s*\+\s*Moxi|Moxi|BBL)\s+(.+?)(?:\s+\d+\s*PK)?$/i);
  return m?.[1]?.trim() ?? null;
}

function extractDeviceDashAreaFromSkuName(skuName: string): string | null {
  const m = skuName.match(/^(?:Sofwave|Ultherapy)\s*[–-]\s*(.+)$/i);
  return m?.[1]?.trim() ?? null;
}

function matchEnergyTreatmentSku(
  product: string,
  region: string,
  skus: SkuWithCategory[],
): SkuWithCategory | null {
  const area = region.trim();
  const device = product.trim();
  if (!device || !area) return null;

  const laserPrefix = getLaserDevicePrefixFromProduct(device);
  if (laserPrefix) {
    const match = skus.find((s) => {
      if (/\d+\s*PK/i.test(s.name)) return false;
      if (getLaserPrefixFromSkuName(s.name) !== laserPrefix) return false;
      return treatmentAreaMatchesSkuArea(extractLaserAreaFromSkuName(s.name), area);
    });
    if (match) return match;
  }

  if (/^sofwave/i.test(device)) {
    const match = skus.find(
      (s) =>
        /^Sofwave\s*[–-]/i.test(s.name) &&
        treatmentAreaMatchesSkuArea(extractDeviceDashAreaFromSkuName(s.name), area),
    );
    if (match) return match;
  }

  if (/^ultherapy/i.test(device)) {
    const match = skus.find(
      (s) =>
        /^Ultherapy\s*[–-]/i.test(s.name) &&
        treatmentAreaMatchesSkuArea(extractDeviceDashAreaFromSkuName(s.name), area),
    );
    if (match) return match;
  }

  return null;
}

/** Chemical peel area names from the 2025 price list (e.g. Full Face, Full Back). */
export function getChemicalPeelAreasFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Chemical Peel");
  if (!section) return [];
  const byKey = new Map<string, string>();
  for (const item of section.items) {
    const raw = splitChemicalPeelTypeAndArea(item.name).area;
    if (!raw?.trim()) continue;
    const trimmed = raw.trim();
    const key = normalizeTreatmentAreaKey(trimmed);
    const prev = byKey.get(key);
    if (!prev || trimmed.length < prev.length) {
      byKey.set(key, trimmed);
    }
  }
  return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
}

/** Microneedling/PRFM type names from the 2025 price list (Medical Spa section). Used as Type options for Microneedling so selection matches pricing. Includes all location-specific SKUs (e.g. CA vs Henderson). {@link preferClaremontCaSkus} still prefers CA/clinic-default rows when fuzzy-matching a plan line to a SKU. */
export function getMicroneedlingTypesFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Medical Spa");
  if (!section) return [];
  const names = section.items
    .filter((i) => {
      const n = i.name;
      if (/PRFM\s*Injections/i.test(n) || /PRFM\s*Hair\s*Restoration/i.test(n)) {
        return false;
      }
      if (n === "PRFM Additional Tube") return false;
      return (
        n.includes("Microneedling") ||
        n.includes("PRFM") ||
        n.trim() === "PDGF"
      );
    })
    .map((i) => i.name);

  // Insert the compound option immediately after "PRFM Microneedling" (CA sheet row).
  const prfmIdx = names.indexOf("PRFM Microneedling");
  if (prfmIdx !== -1) {
    names.splice(prfmIdx + 1, 0, "PRFM Microneedling + Additional Tube");
  }

  return names;
}

/** Neurotoxin type names from the 2025 price list (Injectables: Botox, Dysport, Sweating). Used as Type options so selection matches pricing. */
export function getNeurotoxinTypesFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Injectables");
  if (!section) return [];
  const normalized = section.items
    .filter(
      (i) =>
        i.name.includes("Botox") ||
        i.name.includes("Dysport") ||
        i.name.includes("Sweating"),
    )
    .map((i) => {
      if (i.name.includes("Botox 1-Unit")) return "Botox";
      if (i.name.includes("Dysport 1-Unit")) return "Dysport";
      return i.name;
    });
  return Array.from(new Set(normalized));
}

/** Filler type names from the 2025 price list (Injectables: Filler, Voluma, Volux; Other Treatments: Filler Dissolver). Used as Type options so selection matches pricing. */
export function getFillerTypesFromPriceList(): string[] {
  const out: string[] = [];
  for (const section of TREATMENT_PRICE_LIST_2025) {
    if (section.category === "Injectables") {
      section.items
        .filter((i) =>
          /filler|voluma|volux|vollux|vollure|volbella|dissolver|hyaluronidase|juvederm|restylane|revance|rha\d|skinvive|liquid rhinoplasty/i.test(i.name),
        )
        .forEach((i) => out.push(i.name));
    } else if (section.category === "Other Treatments") {
      section.items
        .filter((i) => /dissolver|hyaluronidase/i.test(i.name))
        .forEach((i) => out.push(i.name));
    }
  }
  return out;
}

/** Biostimulants type names from the 2025 price list (Injectables: Radiesse, Sculptra, Skinvive). Type shows base name only; quantity (vials/syringes) is a separate field. */
export function getBiostimulantsTypesFromPriceList(): string[] {
  const section = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Injectables");
  if (!section) return [];
  const normalized: string[] = [];
  for (const i of section.items) {
    if (i.name.includes("Radiesse")) {
      normalized.push("Radiesse");
    } else if (i.name.includes("Sculptra")) {
      normalized.push("Sculptra");
    }
  }
  const out = Array.from(new Set(normalized));
  if (!out.includes("EZ Gel PRF")) out.push("EZ Gel PRF");
  return out;
}

/**
 * Type / product chips for the "Other procedures" plan category (2025 list): PRFM + Skinvive
 * flows first, then Medical Spa add-ons from "Other Treatments", then spider-vein injectables.
 * Facial PRFM site still uses Where (`PRFM_INJECTION_WHERE_OPTIONS`); other lines are one-click SKUs.
 */
export function getOtherProcedureTypesFromPriceList(): string[] {
  const CORE: readonly string[] = [
    "PRFM injections",
    "PRFM scalp (hair restoration)",
    "Skinvive",
  ];
  const seen = new Set<string>();
  const out: string[] = [];
  const add = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    const k = t.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push(t);
  };
  for (const c of CORE) add(c);

  const otherTreat = TREATMENT_PRICE_LIST_2025.find(
    (s) => s.category === "Other Treatments",
  );
  if (otherTreat) {
    const extras = otherTreat.items
      .filter((i) => !/dissolver/i.test(i.name))
      .map((i) => i.name.trim())
      .sort((a, b) => a.localeCompare(b));
    for (const n of extras) add(n);
  }

  const inj = TREATMENT_PRICE_LIST_2025.find((s) => s.category === "Injectables");
  if (inj) {
    const fromInj = inj.items
      .filter(
        (i) =>
          /spider\s*vein/i.test(i.name) ||
          (i.name.trim() === "Skinvive" && !/intradermal/i.test(i.name)),
      )
      .map((i) => i.name.trim())
      .sort((a, b) => a.localeCompare(b));
    for (const n of fromInj) add(n);
  }

  return out;
}

/** Category meta for checkout display (longevity, recovery, sessions). */
export const CHECKOUT_CATEGORY_META: Record<string, CategoryMeta> = {
  Skincare: { longevity: "Ongoing", downtime: "None", sessions: "Ongoing" },
  "Energy Treatment": { longevity: "6–12+ months", downtime: "3–7 days", sessions: "1–6" },
  "Chemical Peel": { longevity: "1–3 months", downtime: "3–7 days", sessions: "1–3" },
  Microneedling: { longevity: "2–4 months", downtime: "1–3 days", sessions: "1–5" },
  Filler: { longevity: "6–18 months", downtime: "1–2 days", sessions: "1–2" },
  Neurotoxin: { longevity: "3–4 months", downtime: "None", sessions: "1" },
  Biostimulants: { longevity: "18–24+ months", downtime: "1–3 days", sessions: "1–3" },
  "Other procedures": { longevity: "Varies", downtime: "1–7 days", sessions: "1–3" },
  "Breast Surgery": { longevity: "Varies", downtime: "1–2 weeks+", sessions: "1" },
  "Facial Surgery": { longevity: "Varies", downtime: "1–2 weeks+", sessions: "1" },
  ...Object.fromEntries(
    JUDGEMD_FACIAL_SURGERY_PLAN_CATEGORIES.filter((c) => c !== "Facial Surgery").map(
      (c) => [c, { longevity: "Varies", downtime: "1–2 weeks+", sessions: "1" }],
    ),
  ),
  "Body Sculpting": { longevity: "Varies", downtime: "1–2 weeks+", sessions: "1" },
  "Vaginal Rejuvenation": { longevity: "Varies", downtime: "1–2 weeks+", sessions: "1" },
  Kybella: { longevity: "Permanent", downtime: "3–7 days", sessions: "1–2" },
  Threadlift: { longevity: "12–18 months", downtime: "3–7 days", sessions: "1" },
};

export function getCategoryMeta(category: string): CategoryMeta | undefined {
  return CHECKOUT_CATEGORY_META[
    normalizePlanTreatmentCategoryForPricing(category?.trim() ?? "")
  ];
}

type SkuWithCategory = TreatmentPriceItem & { category: string };

/** Get SKUs that belong to the given dashboard treatment category. */
function getSkusForDashboardCategory(
  dashboardCategory: string,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): SkuWithCategory[] {
  const dash = normalizePlanTreatmentCategoryForPricing(
    dashboardCategory?.trim() ?? "",
  );
  const sections = DASHBOARD_TO_PRICE_SECTIONS[dash];
  if (!sections?.length) return [];
  const out: SkuWithCategory[] = [];
  for (const section of priceList) {
    if (!sections.includes(section.category)) continue;
    const items = section.items;
    if (dash === "Neurotoxin") {
      items
        .filter(injectableMatchesNeurotoxinSku)
        .forEach((i) => out.push({ ...i, category: section.category }));
    } else if (dash === "Filler") {
      items
        .filter(injectableMatchesFillerSku)
        .forEach((i) => out.push({ ...i, category: section.category }));
    } else if (dash === "Biostimulants") {
      items
        .filter(injectableMatchesBiostimulantSku)
        .forEach((i) => out.push({ ...i, category: section.category }));
    } else if (dash === "Other procedures") {
      if (section.category === "Medical Spa") {
        items
          .filter((i) => {
            const n = i.name;
            return (
              /PRFM\s*Injections/i.test(n) || /PRFM\s*Hair\s*Restoration/i.test(n)
            );
          })
          .forEach((i) => out.push({ ...i, category: section.category }));
      } else if (section.category === "Injectables") {
        items
          .filter(
            (i) =>
              (i.name.includes("Skinvive") && !/intradermal/i.test(i.name)) ||
              /spider\s*vein/i.test(i.name),
          )
          .forEach((i) => out.push({ ...i, category: section.category }));
      } else if (section.category === "Other Treatments") {
        items
          .filter((i) => !/dissolver/i.test(i.name))
          .forEach((i) => out.push({ ...i, category: section.category }));
      }
    } else if (dash === "Microneedling") {
      items
        .filter((i) => {
          const n = i.name;
          if (/PRFM\s*Injections/i.test(n) || /PRFM\s*Hair\s*Restoration/i.test(n)) {
            return false;
          }
          return (
            i.name.includes("Microneedling") ||
            i.name.includes("PRFM") ||
            i.name.trim() === "PDGF"
          );
        })
        .forEach((i) => out.push({ ...i, category: section.category }));
    } else {
      let rowItems = items;
      if (isJudgeMdFacialPlanSubcategory(dash) && section.category === "Facial Surgery") {
        const allow = new Set(getJudgeMdProductOptionsForSurgeryCategory(dash));
        rowItems = items.filter((i) => allow.has(i.name));
      }
      rowItems.forEach((i) => out.push({ ...i, category: section.category }));
    }
  }
  if (dash === "Other procedures") {
    for (const section of priceList) {
      if (!JUDGEMD_SURGERY_CAT_SET.has(section.category)) continue;
      section.items.forEach((i) => out.push({ ...i, category: section.category }));
    }
  }
  return out;
}

/** Normalize for fuzzy match: lowercase, collapse spaces, remove some punctuation. */
function norm(s: string): string {
  return (s ?? "")
    .toLowerCase()
    .replace(/[\s–-]+/g, " ")
    .trim();
}

function getBlepharoplastyVariant(value: string): "upper" | "lower" | "quad" | "generic" | null {
  const n = norm(value);
  if (!n.includes("blepharoplasty") && !n.includes("eyelid surgery")) return null;
  if (
    n.includes("quad") ||
    n.includes("upper & lower") ||
    n.includes("upper and lower") ||
    (n.includes("upper") && n.includes("lower"))
  ) {
    return "quad";
  }
  if (n.includes("upper")) return "upper";
  if (n.includes("lower")) return "lower";
  return "generic";
}

/** Check if plan product/region text matches SKU name (contains or is contained). */
function skuNameMatches(skuName: string, productOrRegion: string): boolean {
  if (!productOrRegion?.trim()) return false;
  const a = norm(skuName);
  const b = norm(productOrRegion);
  const skuBleph = getBlepharoplastyVariant(skuName);
  const queryBleph = getBlepharoplastyVariant(productOrRegion);
  if (skuBleph && queryBleph) {
    if (skuBleph === "generic" || queryBleph === "generic") return true;
    return skuBleph === queryBleph;
  }
  if (/(?:lipo|liposuction).*subment/i.test(b) && /liposuction.*subment/i.test(a)) {
    return true;
  }
  if (a.includes(b) || b.includes(a)) return true;
  if (b.includes("face") && (a.includes("full face") || a.includes("face"))) return true;
  if (b.includes("neck") && a.includes("neck")) return true;
  if (b.includes("chest") && a.includes("chest")) return true;
  if (b.includes("moxi") && a.includes("moxi")) return true;
  if (b.includes("bbl") && a.includes("bbl")) return true;
  if (b.includes("botox") && a.includes("botox")) return true;
  if (b.includes("dysport") && a.includes("dysport")) return true;
  if ((b.includes("daxxify") || b.includes("doxify")) && /daxxify|doxify/i.test(a)) return true;
  if (b.includes("filler") && (a.includes("filler") || a.includes("Voluma") || a.includes("Volux"))) return true;
  if (b.includes("radiasse") && a.includes("Radiesse")) return true;
  if (b.includes("sculptra") && a.includes("Sculptra")) return true;
  if (b.includes("microneedling") && a.includes("Microneedling")) return true;
  if (b.includes("peel") && a.includes("Peel")) return true;
  if (b.includes("facial") && a.includes("Facial")) return true;
  if (/fat transfers? to (the )?face/i.test(b) && /fat transfer to face/i.test(a))
    return true;
  if (/fat transfers? (to )?(the )?face/i.test(b) && /fat transfer to face/i.test(a))
    return true;
  return false;
}

/** Default neurotoxin units for quoting when patient doesn't specify (typical multi-area treatment). */
export const DEFAULT_NEUROTOXIN_UNITS_FOR_QUOTE = 35;

/** Prefer Claremont/California pricing over Henderson when both exist. */
function preferClaremontCaSkus<T extends { name: string }>(skus: T[]): T[] {
  return [...skus].sort((a, b) => {
    const aH = a.name.includes("Henderson") ? 1 : 0;
    const bH = b.name.includes("Henderson") ? 1 : 0;
    return aH - bH;
  });
}

/** Skin-booster Skinvive base row (not intradermal filler, not add-on syringe line). */
function findSkinviveSkinBoosterBaseSku<T extends { name: string }>(skus: T[]): T | undefined {
  const exact = skus.find((s) => s.name.trim() === "Skinvive");
  if (exact && !/intradermal/i.test(exact.name)) return exact;
  return skus.find(
    (s) =>
      /skinvive/i.test(s.name) &&
      !/intradermal/i.test(s.name) &&
      !/add-on/i.test(s.name),
  );
}

function findSkinviveAddonSyringeSku<T extends { name: string }>(skus: T[]): T | undefined {
  return skus.find((s) => /Skinvive\s*Add-On\s*Syringe/i.test(s.name));
}

/**
 * Skinvive skin booster: **no 1-syringe option**. Unset quantity → **2 syringes / $750** (base SKU).
 * **3 syringes** = $750 + $375 add-on = $1,125; each syringe beyond 2 uses add-on SKU (+$375).
 */
function matchSkinviveSkinBoosterBySyringeCount<T extends { name: string; price: number }>(
  skus: T[],
  quantityStr: string,
): {
  sku: T;
  totalPrice: number;
  isPerUnit?: boolean;
  unitPrice?: number;
  quantity: number;
} | null {
  const skinviveBase = findSkinviveSkinBoosterBaseSku(skus);
  const skinviveAddon = findSkinviveAddonSyringeSku(skus);
  if (!skinviveBase) return null;
  const trimmed = quantityStr.trim();
  const parsed = trimmed ? parseInt(trimmed, 10) : NaN;
  const hasExplicit = !Number.isNaN(parsed) && parsed > 0;
  let n: number;
  if (!hasExplicit) {
    n = 2;
  } else if (parsed === 1) {
    return null;
  } else {
    n = parsed;
  }
  if (n > 2 && !skinviveAddon) return null;
  const totalPrice =
    skinviveBase.price + Math.max(0, n - 2) * (skinviveAddon?.price ?? 0);
  if (n > 2) {
    return {
      sku: skinviveBase,
      totalPrice,
      isPerUnit: true,
      unitPrice: skinviveAddon!.price,
      quantity: n,
    };
  }
  return {
    sku: skinviveBase,
    totalPrice,
    quantity: n,
  };
}

function neurotoxinProductRequiresUnits(product: string): boolean {
  const p = (product ?? "").trim().toLowerCase();
  if (!p) return false;
  return /botox|dysport|daxxify|doxify|baby\s*tox/.test(p);
}

function matchJudgeMdFixedNeurotoxinSku(
  product: string,
  skus: SkuWithCategory[],
): SkuWithCategory | null {
  const p = (product ?? "").trim().toLowerCase();
  if (!p) return null;
  if (/lip\s*flip|gummy\s*smile|brow\s*lift|browlift/.test(p)) {
    return (
      skus.find((s) =>
        /lip\s*flip.*gummy\s*smile.*browlift/i.test(s.name),
      ) ?? null
    );
  }
  if (/masseter/.test(p)) {
    return skus.find((s) => /masseter/i.test(s.name)) ?? null;
  }
  if (/trapezius|trap/.test(p)) {
    return skus.find((s) => /trapezius/i.test(s.name)) ?? null;
  }
  return null;
}

/** Match a plan item to a specific SKU and return price (total or per-unit × quantity). */
export function matchPlanItemToSku(
  item: PlanItemForPricing,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): {
  sku: SkuWithCategory;
  totalPrice: number;
  isPerUnit?: boolean;
  unitPrice?: number;
  quantity?: number;
} | null {
  const treatment = normalizePlanTreatmentCategoryForPricing(
    (item.treatment ?? "").trim(),
  );
  if (!treatment) return null;
  // Skincare with a specific product (e.g. Retinol, Vitamin C) uses boutique pricing, not the 2025 facial price list.
  if (treatment === "Skincare" && (item.product ?? "").trim()) return null;
  let skus = getSkusForDashboardCategory(treatment, priceList);
  skus = preferClaremontCaSkus(skus);
  if (skus.length === 0) return null;

  const product = (item.product ?? "").trim();
  if (treatment === "Biostimulants" && product && /skinvive/i.test(product)) {
    skus = preferClaremontCaSkus(
      getSkusForDashboardCategory("Other procedures", priceList).filter((s) => /Skinvive/i.test(s.name)),
    );
  }
  const region = (item.region ?? "").trim();
  const quantityStr = (item.quantity ?? "").trim();
  const qty = quantityStr ? parseInt(quantityStr, 10) : NaN;

  if (treatment === "Neurotoxin" && product) {
    const fixedJudgeMdSku = matchJudgeMdFixedNeurotoxinSku(product, skus);
    if (fixedJudgeMdSku) {
      return { sku: fixedJudgeMdSku, totalPrice: fixedJudgeMdSku.price };
    }
    if (/baby\s*tox/i.test(product)) {
      const babyToxSku = skus.find((s) => /baby\s*tox/i.test(s.name));
      const babyToxQty = !Number.isNaN(qty) && qty > 0 ? qty : 0;
      if (babyToxSku && babyToxQty > 0) {
        return {
          sku: babyToxSku,
          totalPrice: babyToxSku.price * babyToxQty,
          isPerUnit: true,
          unitPrice: babyToxSku.price,
          quantity: babyToxQty,
        };
      }
      if (babyToxSku) return null;
    }
  }

  // Per-unit SKUs (Botox, Dysport) – use CA 1-Unit pricing only, not Henderson vials
  const perUnitSkus = skus.filter(
    (s) =>
      !s.name.includes("Henderson") &&
      (s.name.includes("1-Unit") ||
        (s.name.includes("Botox") && s.name.includes("Unit")) ||
        (s.name.includes("Dysport") && s.name.includes("Unit")) ||
        (/daxxify|doxify/i.test(s.name) && s.name.includes("Unit"))),
  );
  // No quantity fallback for neurotoxin — blank units means price is unknown (shown as "Price varies").
  const effectiveQty = !Number.isNaN(qty) && qty > 0 ? qty : 0;
  if (perUnitSkus.length > 0 && effectiveQty > 0) {
    // Do not assume Botox/Dysport 1-unit × default units when no product/SKU is chosen.
    if (treatment === "Neurotoxin" && !product) {
      /* fall through to generic matching / null */
    } else {
      const preferBotox =
        /botox|tox/i.test(product) && !/dysport|daxxify|doxify/i.test(product);
      const preferDysport = /dysport/i.test(product);
      const preferDaxxify = /daxxify|doxify/i.test(product);
      let chosen = perUnitSkus[0];
      if (preferBotox) chosen = perUnitSkus.find((s) => s.name.includes("Botox")) ?? chosen;
      if (preferDysport) chosen = perUnitSkus.find((s) => s.name.includes("Dysport")) ?? chosen;
      if (preferDaxxify)
        chosen = perUnitSkus.find((s) => /daxxify|doxify/i.test(s.name)) ?? chosen;
      const totalPrice = chosen.price * effectiveQty;
      return {
        sku: chosen,
        totalPrice,
        isPerUnit: true,
        unitPrice: chosen.price,
        quantity: effectiveQty,
      };
    }
  }

  // Microneedling: "PRFM Microneedling + Additional Tube" = PRFM Microneedling ($775) + PRFM Additional Tube ($250) = $1025.
  // Use CA/Claremont pricing only (Henderson SKUs are excluded via preferClaremontCaSkus above).
  if (treatment === "Microneedling" && /additional\s*tube/i.test(product)) {
    const prfmSku = skus.find((s) => s.name === "PRFM Microneedling");
    const addOnSku = skus.find((s) => s.name === "PRFM Additional Tube");
    if (prfmSku && addOnSku) {
      return { sku: prfmSku, totalPrice: prfmSku.price + addOnSku.price };
    }
  }

  // Microneedling: PDGF with microneedling = Microneedling ($500) + PDGF ($495) = $995. (PDGF is not sold standalone.)
  if (treatment === "Microneedling" && product) {
    const pLower = product.toLowerCase();
    const mentionsPdgf = /\bpdgf\b/i.test(product);
    const mentionsMn = /microneedl/i.test(pLower);
    if (mentionsPdgf && mentionsMn) {
      const baseSku = skus.find(
        (s) => s.name === "Microneedling" && !s.name.includes("Henderson"),
      );
      const pdgfSku = skus.find((s) => s.name.trim() === "PDGF");
      if (baseSku && pdgfSku) {
        return {
          sku: baseSku,
          totalPrice: baseSku.price + pdgfSku.price,
        };
      }
    }
  }

  if (treatment === "Other procedures" && product) {
    const pTrim = product.trim();
    const pLow = pTrim.toLowerCase();
    /** One-off add-ons (Nerve Block, Cortisone Shot, …) match sheet row name exactly. */
    const exactOtherSku = skus.find((s) => norm(s.name) === norm(pTrim));
    if (exactOtherSku) return { sku: exactOtherSku, totalPrice: exactOtherSku.price };
    /** 2025 sheet: Nerve Block $25 — map word variants (spacing, "NB" alone is not matched). */
    if (/nerve\s*block/i.test(pTrim)) {
      const nerveSku = skus.find((s) => norm(s.name) === norm("Nerve Block"));
      if (nerveSku) return { sku: nerveSku, totalPrice: nerveSku.price };
    }
    /** 2025 sheet: Cortisone Shot $50 — map common variants ("Cortisone", "Cortisone injection"). */
    if (/cortisone/i.test(pTrim)) {
      const cortisoneSku = skus.find(
        (s) => norm(s.name) === norm("Cortisone Shot"),
      );
      if (cortisoneSku)
        return { sku: cortisoneSku, totalPrice: cortisoneSku.price };
    }
    const rLow = (region ?? "").toLowerCase();
    if (/skinvive/i.test(pLow)) {
      return matchSkinviveSkinBoosterBySyringeCount(skus, quantityStr);
    }
    if (
      /prfm\s*scalp|scalp|hair\s*restoration/i.test(pLow) ||
      /prfm injections\s*[–-]\s*scalp/i.test(pLow)
    ) {
      const hair = skus.find(
        (s) => /PRFM\s*Hair\s*Restoration/i.test(s.name) && !s.name.includes("Henderson"),
      );
      if (hair) return { sku: hair, totalPrice: hair.price };
    }
    if (
      /prfm injections/i.test(pLow) ||
      /nasolabial|under\s*eye/i.test(pLow) ||
      /nasolabial|under\s*eye/i.test(rLow)
    ) {
      const inj = skus.find(
        (s) => s.name === "PRFM Injections" && !s.name.includes("Henderson"),
      );
      if (inj) return { sku: inj, totalPrice: inj.price };
    }
  }

  // Filler: exact type label → exact SKU so "Voluma" / "Volux" are not absorbed by
  // "Fillers (except Voluma & Volux)" via substring matching.
  if (treatment === "Filler" && product) {
    const pt = product.trim();
    const exactSku = skus.find((s) => s.name.toLowerCase() === pt.toLowerCase());
    if (exactSku) {
      const fillerQty = !Number.isNaN(qty) && qty > 0 ? qty : 1;
      return {
        sku: exactSku,
        totalPrice: exactSku.price * fillerQty,
        isPerUnit: true,
        unitPrice: exactSku.price,
        quantity: fillerQty,
      };
    }
  }

  // Energy Treatment: device type + area must match the priced SKU (not fuzzy "moxi" → first row).
  if (treatment === "Energy Treatment" && product && region) {
    const energyMatch = matchEnergyTreatmentSku(product, region, skus);
    if (energyMatch) return { sku: energyMatch, totalPrice: energyMatch.price };
  }

  // Chemical Peel: when both type and area are selected, require both to match the SKU.
  if (treatment === "Chemical Peel" && product && region) {
    const byTypeAndArea = skus.find((s) => {
      const { type, area: skuArea } = splitChemicalPeelTypeAndArea(s.name);
      return (
        skuNameMatches(type || s.name, product) &&
        treatmentAreaMatchesSkuArea(skuArea, region)
      );
    });
    if (byTypeAndArea) return { sku: byTypeAndArea, totalPrice: byTypeAndArea.price };
  }

  // Biostimulants (Radiesse/Sculptra): type is base name only; quantity selects vial/syringe count SKU
  if (treatment === "Biostimulants" && product) {
    const bioQty = !Number.isNaN(qty) && qty > 0 ? qty : 1;
    if (/radiesse/i.test(product)) {
      const radiesseSkus = skus.filter((s) => s.name.includes("Radiesse"));
      const exact =
        bioQty === 1
          ? radiesseSkus.find((s) => s.name === "Radiesse")
          : radiesseSkus.find((s) => {
              const m = s.name.match(/Radiesse\s*[–-]\s*(\d+)\s*Syringe/i);
              return m && parseInt(m[1], 10) === bioQty;
            });
      if (exact) return { sku: exact, totalPrice: exact.price };
    }
    if (/ez\s*gel/i.test(product)) {
      const ez = skus.find((s) => /ez\s*gel/i.test(s.name));
      if (ez) return { sku: ez, totalPrice: ez.price };
    }
    if (/sculptra/i.test(product)) {
      const vialTotal = getSculptraVialCountForPlanPricing(item);
      const sculptraQty =
        !Number.isNaN(vialTotal) && vialTotal > 0 ? vialTotal : 1;
      const sculptraSkus = skus.filter((s) => {
        if (!s.name.includes("Sculptra")) return false;
        if (/buttock/i.test(s.name)) return /buttock/i.test(product);
        return !/buttock/i.test(product);
      });
      const exact = sculptraSkus.find((s) => {
        const m = s.name.match(/Sculptra\s*[–-]\s*(\d+)\s*Vials?/i);
        return m && parseInt(m[1], 10) === sculptraQty;
      });
      if (exact) return { sku: exact, totalPrice: exact.price };
      const oneVial = sculptraSkus.find((s) => /Sculptra\s*[–-]\s*1\s*Vial/i.test(s.name));
      if (oneVial) {
        return {
          sku: oneVial,
          totalPrice: oneVial.price * sculptraQty,
          isPerUnit: true,
          unitPrice: oneVial.price,
          quantity: sculptraQty,
        };
      }
    }
    if (/renuva/i.test(product)) {
      const ren = skus.find((s) => /Renuva/i.test(s.name));
      if (ren) return { sku: ren, totalPrice: ren.price };
    }
    if (/skinvive/i.test(product)) {
      return matchSkinviveSkinBoosterBySyringeCount(skus, quantityStr);
    }
  }

  // Try match by product then region
  const searchTerms = [product, region].filter(Boolean);
  let matched: SkuWithCategory | null = null;
  if (searchTerms.length > 0) {
    // For compound product names (e.g. "Moxi + BBL"), prefer a SKU that matches
    // ALL components over the first partial match. Without this, "Moxi Full Face"
    // beats "BBL + Moxi Full Face" because both contain "moxi".
    if (product.includes("+")) {
      const parts = product.split("+").map((p) => p.trim()).filter(Boolean);
      const bestMatch = skus.find((s) => parts.every((p) => skuNameMatches(s.name, p)));
      if (bestMatch) matched = bestMatch;
    }

    if (!matched) {
      for (const term of searchTerms) {
        const found = skus.find((s) => skuNameMatches(s.name, term));
        if (found) {
          matched = found;
          break;
        }
      }
    }
    if (!matched) {
      const term = searchTerms[0];
      const byContains = skus.filter((s) => skuNameMatches(s.name, term));
      if (byContains.length > 0) matched = byContains[0];
    }
  }
  if (!matched) {
    if (skus.length === 1) {
      matched = skus[0];
    } else if (
      !product &&
      (treatment === "Neurotoxin" ||
        treatment === "Filler" ||
        treatment === "Biostimulants")
    ) {
      matched = null;
    } else if (
      (treatment === "Other procedures" || isJudgeMdSurgeryPlanCategory(treatment)) &&
      (product ?? "").trim().length > 0
    ) {
      matched = null;
    } else {
      matched = skus[0];
    }
  }
  if (!matched) return null;
  // Neurotoxin per-unit SKUs (Botox/Dysport 1-Unit): require units. Generic name matching can
  // still resolve a 1-Unit SKU; without units, list price must not be shown as the line total.
  if (treatment === "Neurotoxin") {
    const isPerUnitNeuroSku =
      !matched.name.includes("Henderson") &&
      (matched.name.includes("1-Unit") ||
        (matched.name.includes("Botox") && matched.name.includes("Unit")) ||
        (matched.name.includes("Dysport") && matched.name.includes("Unit")) ||
        (/daxxify|doxify/i.test(matched.name) && matched.name.includes("Unit")));
    const hasUnits = !Number.isNaN(qty) && qty > 0;
    if (isPerUnitNeuroSku && !hasUnits) return null;
  }
  // Filler: quantity is in Syringes; if set, price should scale by quantity.
  if (treatment === "Filler" && !Number.isNaN(qty) && qty > 0) {
    return {
      sku: matched,
      totalPrice: matched.price * qty,
      isPerUnit: true,
      unitPrice: matched.price,
      quantity: qty,
    };
  }
  return { sku: matched, totalPrice: matched.price };
}

/** Formatted total for a matched SKU (no per-unit breakdown in UI). */
export function formatSkuMatchDisplayPrice(match: {
  totalPrice: number;
  isPerUnit?: boolean;
  unitPrice?: number;
  quantity?: number;
}): string {
  return formatPrice(match.totalPrice);
}

/**
 * Estimated price range for a single plan item based on treatment category (2025 list).
 */
export function getEstimatedPriceForPlanItem(
  item: PlanItemForPricing
): { min: number; max: number; displayPrice: string } | undefined {
  const range = getPriceRangeNumeric2025(item.treatment?.trim() ?? "");
  if (!range) return undefined;
  if (range.min === 0 && range.max === 0) return undefined;
  return {
    min: range.min,
    max: range.max,
    displayPrice:
      range.min === range.max
        ? formatPrice(range.min)
        : `${formatPrice(range.min)}–${formatPrice(range.max)}`,
  };
}

/**
 * Build checkout summary: line items with estimated prices and total range.
 * Items without a matching price are omitted from totals but can still be shown with "Price varies".
 */
export function getCheckoutSummary(
  items: (PlanItemForPricing & { id?: string })[],
  getLabel: (item: PlanItemForPricing & { id?: string }) => string
): {
  lineItems: CheckoutLineItem[];
  totalMin: number;
  totalMax: number;
  hasUnknownPrices: boolean;
} {
  const lineItems: CheckoutLineItem[] = [];
  let totalMin = 0;
  let totalMax = 0;
  let hasUnknownPrices = false;
  for (const item of items) {
    const label = getLabel(item);
    const est = getEstimatedPriceForPlanItem(item);
    if (est) {
      lineItems.push({
        label,
        min: est.min,
        max: est.max,
        displayPrice: est.displayPrice,
      });
      totalMin += est.min;
      totalMax += est.max;
    } else {
      lineItems.push({
        label,
        min: 0,
        max: 0,
        displayPrice: "Price varies",
      });
      hasUnknownPrices = true;
    }
  }
  return { lineItems, totalMin, totalMax, hasUnknownPrices };
}

/**
 * Rich checkout line: SKU-level price plus longevity, recovery, sessions for detail view.
 */
export interface CheckoutLineItemDetail {
  /** Display label (e.g. "Laser • Full Face") */
  label: string;
  /** Matched SKU name from price list (e.g. "Moxi Full Face") */
  skuName?: string;
  /** Note from price list (e.g. "CA locations only") */
  skuNote?: string;
  /** Single price for this line (for total) */
  price: number;
  /** Formatted price for display (e.g. "$550") */
  displayPrice: string;
  /** Longevity (e.g. "6–12+ months") */
  longevity?: string;
  /** Recovery/downtime (e.g. "3–7 days") */
  downtime?: string;
  /** Sessions (e.g. "1–6") */
  sessions?: string;
  /** Optional photo URL for treatment (caller can attach) */
  photoUrl?: string;
  /** True if no SKU matched (price is 0, displayPrice may be "Price varies") */
  isEstimate?: boolean;
  /** Product description (skincare only; when set, longevity/recovery/sessions are omitted) */
  description?: string;
  /** Human-readable explanation of why pricing is unavailable (shown as an actionable warning in checkout UI). */
  missingInfo?: string;
  /** Quote UI grouping: `skincare` = Skin Boutique retail product line only; `treatment` = everything else (including facials under Skincare category) */
  quoteLineKind?: "skincare" | "treatment";
  /**
   * When true (set when sending the shared plan link), the patient-facing quote hides this
   * line’s dollar amount; `price` is still stored for staff/analytics. Omitted = show price.
   */
  hidePriceFromPatient?: boolean;
  /**
   * When sending the shared plan, staff may set a patient-facing amount that differs from
   * {@link price} (e.g. corrected quote). Patient UI uses this for display and totals when
   * present and {@link hidePriceFromPatient} is false. Omitted = use `price`.
   */
  patientPriceOverride?: number;
}

/** Skincare boutique product info for checkout (from getSkincareCarouselItems / TREATMENT_BOUTIQUE_SKINCARE). */
export interface SkincareProductInfo {
  price?: number;
  displayPrice: string;
  imageUrl?: string;
  productLabel: string;
  /** Short description (shown instead of longevity/recovery/sessions for skincare) */
  description?: string;
}

/**
 * Build checkout summary with SKU-level prices and category meta (longevity, downtime, sessions).
 * Uses specific SKU when product/region/quantity match; otherwise falls back to category range.
 * For Skincare + product, pass getSkincareProductInfo to use boutique product name, price, and image (not the 2025 facial list).
 * @param priceList Effective provider sheet (defaults to 2025); use {@link getEffectivePriceList} for JudgeMD vs default.
 */
export function getCheckoutSummaryWithSkus(
  items: (PlanItemForPricing & { id?: string })[],
  getLabel: (item: PlanItemForPricing & { id?: string }) => string,
  getSkincareProductInfo?: (productName: string) => SkincareProductInfo | null,
  priceList: { category: string; items: TreatmentPriceItem[] }[] = TREATMENT_PRICE_LIST_2025,
): {
  lineItems: CheckoutLineItemDetail[];
  total: number;
  hasUnknownPrices: boolean;
} {
  const lineItems: CheckoutLineItemDetail[] = [];
  let total = 0;
  let hasUnknownPrices = false;
  const category = (item: PlanItemForPricing) =>
    normalizePlanTreatmentCategoryForPricing((item.treatment ?? "").trim());
  const meta = (cat: string) => getCategoryMeta(cat);

  for (const item of items) {
    const label = getLabel(item);
    const cat = category(item);
    const categoryMeta = meta(cat);
    const productName = (item.product ?? "").trim();

    /** Retail Skin Boutique products only — not in-office facials under the Skincare category. */
    const skincareLineKind = "skincare" as const;
    const treatmentLineKind = "treatment" as const;

    if (cat === "Skincare" && productName && getSkincareProductInfo) {
      const skincare = getSkincareProductInfo(productName);
      if (skincare) {
        lineItems.push({
          label: skincare.productLabel,
          skuName: skincare.productLabel,
          price: skincare.price ?? 0,
          displayPrice: skincare.displayPrice,
          photoUrl: skincare.imageUrl,
          isEstimate: !skincare.price,
          description: skincare.description,
          quoteLineKind: skincareLineKind,
          /* longevity/downtime/sessions omitted for skincare products */
        });
        total += skincare.price ?? 0;
        if (!skincare.price) hasUnknownPrices = true;
        continue;
      }
    }

    const match = matchPlanItemToSku(item, priceList);

    if (match) {
      const displayPrice = formatSkuMatchDisplayPrice(match);
      const isPerUnitNeuro =
        match.isPerUnit && /1-Unit|Unit/i.test(match.sku.name);
      const region = (item.region ?? "").trim();
      const neuroBrand = match.sku.name.includes("Dysport")
        ? "Dysport"
        : match.sku.name.includes("Botox")
          ? "Botox"
          : (item.treatment ?? "Neurotoxin").trim();
      const skuName = isPerUnitNeuro
        ? region
          ? `${neuroBrand} to ${region}`
          : neuroBrand
        : match.sku.name;
      lineItems.push({
        label,
        skuName,
        skuNote: match.sku.note,
        price: match.totalPrice,
        displayPrice,
        longevity: categoryMeta?.longevity,
        downtime: categoryMeta?.downtime,
        sessions: categoryMeta?.sessions,
        isEstimate: false,
        quoteLineKind: treatmentLineKind,
      });
      total += match.totalPrice;
    } else {
      const wellnest = getWellnestOfferingByTreatmentName(cat);
      if (wellnest) {
        const minPrice = parseWellnestPriceMin(wellnest.pricing);
        const wmeta = getWellnestPeptideMeta(cat);
        lineItems.push({
          label,
          skuName: wellnest.treatmentName,
          price: minPrice,
          displayPrice: wellnest.pricing,
          longevity: wmeta?.longevity,
          downtime: wmeta?.downtime,
          isEstimate: minPrice <= 0,
          quoteLineKind: treatmentLineKind,
        });
        total += minPrice;
        if (minPrice <= 0) hasUnknownPrices = true;
        continue;
      }
      // Neurotoxin: type selected (product set) but units left blank → can't calculate price.
      const quantityStr = (item.quantity ?? "").trim();
      const hasNoUnits = !quantityStr || isNaN(Number(quantityStr)) || Number(quantityStr) <= 0;
      if (
        cat === "Neurotoxin" &&
        productName &&
        neurotoxinProductRequiresUnits(productName) &&
        hasNoUnits
      ) {
        const m = meta(cat);
        lineItems.push({
          label,
          price: 0,
          displayPrice: "Price varies",
          longevity: m?.longevity,
          downtime: m?.downtime,
          sessions: m?.sessions,
          isEstimate: true,
          missingInfo: "Add units to get a price",
          quoteLineKind: treatmentLineKind,
        });
        hasUnknownPrices = true;
        continue;
      }
      const injectableNeedsProductForQuote =
        (cat === "Neurotoxin" ||
          cat === "Filler" ||
          cat === "Biostimulants" ||
          cat === "Other procedures" ||
          isJudgeMdSurgeryPlanCategory(cat)) &&
        !productName;
      if (injectableNeedsProductForQuote) {
        const m = meta(cat);
        const missingInfo =
          cat === "Neurotoxin"
            ? "Select a type (e.g. Botox, Dysport) to get a price"
            : cat === "Filler"
              ? "Select a filler type to get a price"
              : cat === "Other procedures" || isJudgeMdSurgeryPlanCategory(cat)
                ? "Select a procedure type to get a price"
                : "Select a type (e.g. Radiesse, Sculptra) to get a price";
        lineItems.push({
          label,
          price: 0,
          displayPrice: "Price varies",
          longevity: m?.longevity,
          downtime: m?.downtime,
          sessions: m?.sessions,
          isEstimate: true,
          missingInfo,
          quoteLineKind: treatmentLineKind,
        });
        hasUnknownPrices = true;
        continue;
      }
      const range = getPriceRangeNumeric2025(cat, priceList);
      const categoryMetaFallback = meta(cat);
      if (range && (range.min > 0 || range.max > 0)) {
        const displayPrice =
          range.min === range.max
            ? formatPrice(range.min)
            : `${formatPrice(range.min)}–${formatPrice(range.max)}`;
        lineItems.push({
          label,
          price: range.min,
          displayPrice,
          longevity: categoryMetaFallback?.longevity,
          downtime: categoryMetaFallback?.downtime,
          sessions: categoryMetaFallback?.sessions,
          isEstimate: true,
          quoteLineKind: treatmentLineKind,
        });
        total += range.min;
      } else {
        lineItems.push({
          label,
          price: 0,
          displayPrice: "Price varies",
          longevity: categoryMetaFallback?.longevity,
          downtime: categoryMetaFallback?.downtime,
          sessions: categoryMetaFallback?.sessions,
          isEstimate: true,
          missingInfo: "No pricing available for this item",
          quoteLineKind: treatmentLineKind,
        });
        hasUnknownPrices = true;
      }
    }
  }
  return { lineItems, total, hasUnknownPrices };
}

// ---------------------------------------------------------------------------
// Provider-specific pricing (stored as JSON in Airtable "Treatment Pricing")
// ---------------------------------------------------------------------------

/**
 * Shape stored in the Airtable "Treatment Pricing" long-text field.
 * Identical to `TREATMENT_PRICE_LIST_2025` — array of { category, items }.
 */
export type ProviderPricingJson = {
  category: string;
  items: TreatmentPriceItem[];
}[];

/**
 * Safely parse the JSON string from a provider's "Treatment Pricing" field.
 * Returns `null` if the value is empty or unparseable.
 */
export function parseProviderPricingJson(
  raw: string | null | undefined,
): ProviderPricingJson | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    for (const section of parsed) {
      if (typeof section?.category !== "string" || !Array.isArray(section?.items)) return null;
    }
    return parsed as ProviderPricingJson;
  } catch {
    return null;
  }
}

/**
 * Merge provider pricing overrides on top of a base sheet (2025 default or e.g. JudgeMD embedded list).
 * Provider categories entirely replace matching base categories (by name);
 * categories only in base are kept as-is; categories only from the provider are appended.
 */
export function mergeProviderPricingOntoBase(
  providerPricing: ProviderPricingJson | null | undefined,
  base: ProviderPricingJson,
): ProviderPricingJson {
  if (!providerPricing?.length) return base;
  const merged: ProviderPricingJson = [];
  const providerByCategory = new Map(
    providerPricing.map((s) => [s.category, s]),
  );
  for (const defaultSection of base) {
    const override = providerByCategory.get(defaultSection.category);
    merged.push(override ?? defaultSection);
    providerByCategory.delete(defaultSection.category);
  }
  for (const extra of providerByCategory.values()) {
    merged.push(extra);
  }
  return merged;
}

/**
 * Merge provider pricing overrides on top of the hardcoded 2025 defaults.
 * Provider categories entirely replace matching default categories (by name);
 * categories only in defaults are kept as-is; categories only from the provider are appended.
 */
export function mergeProviderPricing(
  providerPricing: ProviderPricingJson | null | undefined,
): ProviderPricingJson {
  return mergeProviderPricingOntoBase(providerPricing, TREATMENT_PRICE_LIST_2025);
}

/**
 * Wellnest peptide / wellness rows from {@link WELLNEST_OFFERINGS} — used as the merge base when
 * {@link isWellnestWellnessProviderCode} matches (same pattern as JudgeMD’s embedded list).
 */
export function getWellnestEmbeddedPriceListBase(): ProviderPricingJson {
  const categoryOrder: string[] = [];
  const byCat = new Map<string, TreatmentPriceItem[]>();
  for (const o of WELLNEST_OFFERINGS) {
    if (!byCat.has(o.category)) {
      categoryOrder.push(o.category);
      byCat.set(o.category, []);
    }
    const priceNum = parseWellnestPriceMin(o.pricing);
    const noteRaw = o.pricing.trim();
    byCat.get(o.category)!.push({
      name: o.treatmentName,
      price: priceNum,
      note: noteRaw || undefined,
    });
  }
  return categoryOrder.map((category) => ({
    category,
    items: byCat.get(category) ?? [],
  }));
}

function getEmbeddedPriceListBaseForProvider(
  providerCode?: string | null,
  providerName?: string | null,
): ProviderPricingJson {
  if (isJudgeMdProviderCode(providerCode ?? undefined)) {
    return JUDGEMD_PRICE_LIST_2026_27;
  }
  if (isWellnestWellnessProviderCode(providerCode ?? undefined)) {
    return getWellnestEmbeddedPriceListBase();
  }
  if (isGravitasProviderCode(providerCode ?? undefined, providerName ?? undefined)) {
    return GRAVITAS_PRICE_LIST_2026;
  }
  return TREATMENT_PRICE_LIST_2025;
}

/**
 * Build the effective price list for a provider: parse their override, merge with defaults.
 * Handles both the old bare-array format and the new ProviderPricingDocument format.
 * Callers pass `provider["Treatment Pricing"]` and, when available, `provider.code` so JudgeMD / Wellnest
 * use their embedded sheet as the merge base.
 */
export function getEffectivePriceList(
  providerTreatmentPricingRaw: string | null | undefined,
  provider?: string | { code?: string | null; name?: string | null } | null,
): ProviderPricingJson {
  const providerCode =
    typeof provider === "string" ? provider : provider?.code ?? undefined;
  const providerName =
    typeof provider === "object" && provider ? provider.name ?? undefined : undefined;
  const base = getEmbeddedPriceListBaseForProvider(providerCode, providerName);
  if (!providerTreatmentPricingRaw?.trim()) return base;
  try {
    const parsed = JSON.parse(providerTreatmentPricingRaw);
    // New document format: { pricing?, recommenderOptions? }
    if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
      return mergeProviderPricingOntoBase(
        Array.isArray(parsed.pricing) ? (parsed.pricing as ProviderPricingJson) : null,
        base,
      );
    }
    // Old format: bare array
    if (Array.isArray(parsed)) return mergeProviderPricingOntoBase(parsed as ProviderPricingJson, base);
    return base;
  } catch {
    return base;
  }
}

/**
 * Serialize a price list back to the JSON string for Airtable.
 * @deprecated Prefer `serializeProviderPricingDocument` so recommenderOptions are preserved.
 */
export function serializePricingJson(pricing: ProviderPricingJson): string {
  return JSON.stringify(pricing, null, 2);
}

// ---------------------------------------------------------------------------
// Combined "Treatment Pricing" document (pricing + custom recommender options)
// ---------------------------------------------------------------------------

/**
 * Full shape of the "Treatment Pricing" long-text field.
 * Old providers may have a bare JSON array (ProviderPricingJson); new entries use
 * this object wrapper so recommender options live alongside pricing overrides.
 *
 * The `recommenderOptions` map stores ONLY values the provider has manually added
 * beyond the static defaults.  Deletions of default options are tracked separately
 * in `recommenderRemovedDefaults`.
 */
export interface ProviderPricingDocument {
  pricing?: ProviderPricingJson;
  /** Provider-added custom option values, keyed by TreatmentRecommenderOptionType. */
  recommenderOptions?: Record<string, string[]>;
  /** Default option values the provider has removed, keyed by TreatmentRecommenderOptionType. */
  recommenderRemovedDefaults?: Record<string, string[]>;
  /** Explicit display order for built-in + custom recommender options. */
  recommenderOptionOrder?: Record<string, string[]>;
}

/**
 * Parse the raw "Treatment Pricing" JSON into a ProviderPricingDocument.
 * Handles the old bare-array format transparently.
 */
export function parseProviderPricingDocument(
  raw: string | null | undefined,
): ProviderPricingDocument {
  if (!raw?.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Old format: bare pricing array — lift it into the document wrapper.
      return { pricing: parsed as ProviderPricingJson };
    }
    if (parsed && typeof parsed === "object") {
      return parsed as ProviderPricingDocument;
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * Extract just the pricing section from the document, falling back to
 * `parseProviderPricingJson` for old-format data.
 */
export function getPricingFromDocument(
  raw: string | null | undefined,
): ProviderPricingJson | null {
  const doc = parseProviderPricingDocument(raw);
  return doc.pricing ?? null;
}

/**
 * Build the effective price list from the document format.
 * Callers pass `provider["Treatment Pricing"]` and, when available, `provider.code`.
 */
export function getEffectivePriceListFromDocument(
  raw: string | null | undefined,
  providerCode?: string | null,
): ProviderPricingJson {
  const base = getEmbeddedPriceListBaseForProvider(providerCode);
  return mergeProviderPricingOntoBase(getPricingFromDocument(raw), base);
}

/**
 * Serialize a ProviderPricingDocument back to the JSON string for Airtable.
 */
export function serializeProviderPricingDocument(
  doc: ProviderPricingDocument,
): string {
  return JSON.stringify(doc, null, 2);
}

/**
 * Return the custom recommender option values for a given optionType.
 */
export function getCustomRecommenderOptions(
  doc: ProviderPricingDocument,
  optionType: string,
): string[] {
  return doc.recommenderOptions?.[optionType] ?? [];
}

/**
 * Return the removed-default values for a given optionType.
 */
export function getRemovedDefaultRecommenderOptions(
  doc: ProviderPricingDocument,
  optionType: string,
): string[] {
  return doc.recommenderRemovedDefaults?.[optionType] ?? [];
}

/** Return the saved display order for a recommender option type. */
export function getRecommenderOptionOrder(
  doc: ProviderPricingDocument,
  optionType: string,
): string[] {
  return doc.recommenderOptionOrder?.[optionType] ?? [];
}

/**
 * Return a new document with `value` added to the custom options for `optionType`.
 * Deduplicates by value (case-sensitive).
 */
export function addCustomRecommenderOption(
  doc: ProviderPricingDocument,
  optionType: string,
  value: string,
): ProviderPricingDocument {
  const existing = getCustomRecommenderOptions(doc, optionType);
  if (existing.includes(value)) return doc;
  // If the value was previously removed as a default, un-remove it instead of duplicating.
  const removed = getRemovedDefaultRecommenderOptions(doc, optionType).filter(
    (v) => v !== value,
  );
  return {
    ...doc,
    recommenderOptions: {
      ...(doc.recommenderOptions ?? {}),
      [optionType]: [...existing, value],
    },
    recommenderRemovedDefaults: {
      ...(doc.recommenderRemovedDefaults ?? {}),
      [optionType]: removed,
    },
  };
}

/** Return a new document with explicit ordering for all visible values in an option section. */
export function setRecommenderOptionOrder(
  doc: ProviderPricingDocument,
  optionType: string,
  orderedValues: string[],
): ProviderPricingDocument {
  const deduped = Array.from(
    new Set(orderedValues.map((v) => v.trim()).filter(Boolean)),
  );
  return {
    ...doc,
    recommenderOptionOrder: {
      ...(doc.recommenderOptionOrder ?? {}),
      [optionType]: deduped,
    },
  };
}

/**
 * Return a new document with `value` removed from both custom options and
 * (if it was a default value) added to the removed-defaults list.
 */
export function removeRecommenderOption(
  doc: ProviderPricingDocument,
  optionType: string,
  value: string,
  isDefault: boolean,
): ProviderPricingDocument {
  const custom = getCustomRecommenderOptions(doc, optionType).filter(
    (v) => v !== value,
  );
  const removed = getRemovedDefaultRecommenderOptions(doc, optionType);
  const newRemoved =
    isDefault && !removed.includes(value) ? [...removed, value] : removed;
  return {
    ...doc,
    recommenderOptions: {
      ...(doc.recommenderOptions ?? {}),
      [optionType]: custom,
    },
    recommenderRemovedDefaults: {
      ...(doc.recommenderRemovedDefaults ?? {}),
      [optionType]: newRemoved,
    },
  };
}

/**
 * Return a new document with `oldValue` renamed to `newValue` inside custom options.
 */
export function renameCustomRecommenderOption(
  doc: ProviderPricingDocument,
  optionType: string,
  oldValue: string,
  newValue: string,
): ProviderPricingDocument {
  const custom = getCustomRecommenderOptions(doc, optionType).map((v) =>
    v === oldValue ? newValue : v,
  );
  return {
    ...doc,
    recommenderOptions: {
      ...(doc.recommenderOptions ?? {}),
      [optionType]: custom,
    },
  };
}

// ---------------------------------------------------------------------------
// SKU-level options for the treatment recommender
// ---------------------------------------------------------------------------

/** One SKU option for display as a recommender chip (name only; price on {@link SkuOption.price}). */
export interface SkuOption {
  /** Display label for the chip (SKU name, no price). */
  label: string;
  /** Just the SKU name without price (for Airtable value). */
  skuName: string;
  /** Price (for sort/display). */
  price: number;
  /** Price list category this came from (e.g. "Injectables"). */
  sheetCategory: string;
  /** Dashboard category (e.g. "Biostimulants"). */
  dashboardCategory: DashboardTreatmentCategory;
}

/**
 * Get all SKU-level options for a dashboard treatment category from the effective price list.
 * Chip labels are SKU names only (no price); {@link SkuOption.price} is still set for checkout matching.
 */
export function getSkuOptionsForCategory(
  dashboardCategory: DashboardTreatmentCategory,
  priceList?: ProviderPricingJson,
): SkuOption[] {
  const effectiveList = priceList ?? TREATMENT_PRICE_LIST_2025;
  const sections = DASHBOARD_TO_PRICE_SECTIONS[dashboardCategory];
  if (!sections?.length) return [];

  const out: SkuOption[] = [];
  for (const section of effectiveList) {
    if (!sections.includes(section.category)) continue;
    const items = section.items;
    const filtered = filterSkusForDashboardCategory(
      dashboardCategory,
      items,
      section.category,
    );
    for (const item of filtered) {
      out.push({
        label: item.name,
        skuName: item.name,
        price: item.price,
        sheetCategory: section.category,
        dashboardCategory,
      });
    }
  }
  return out;
}

/**
 * Concise biostimulant labels for the treatment recommender (product family only; syringes/vials use quantity).
 * Derived from the effective list so provider pricing overrides are respected.
 */
export function getBiostimulantTypeOptionLabels(
  priceList?: ProviderPricingJson,
): string[] {
  const skus = getSkuOptionsForCategory("Biostimulants", priceList);
  const types = new Set<string>();
  for (const s of skus) {
    const n = s.label;
    if (n.includes("Radiesse")) types.add("Radiesse");
    else if (n.includes("Sculptra")) types.add("Sculptra");
    else if (/ez\s*gel/i.test(n)) types.add("EZ Gel PRF");
  }
  const order = ["Radiesse", "Sculptra", "EZ Gel PRF"];
  return order.filter((t) => types.has(t));
}

/**
 * Concise neurotoxin labels (e.g. Botox / Dysport instead of per-unit SKU names).
 */
export function getNeurotoxinTypeOptionLabels(
  priceList?: ProviderPricingJson,
): string[] {
  const skus = getSkuOptionsForCategory("Neurotoxin", priceList);
  const labels = new Set<string>();
  for (const s of skus) {
    const n = s.label;
    if (n.includes("Botox 1-Unit") || (n.includes("Botox") && n.includes("Unit")))
      labels.add("Botox");
    else if (
      n.includes("Dysport 1-Unit") ||
      (n.includes("Dysport") && n.includes("Unit"))
    )
      labels.add("Dysport");
    else if (/daxxify|doxify/i.test(n)) labels.add("Daxxify");
    else if (/baby\s*tox/i.test(n)) labels.add("Baby Tox");
    else if (/lip\s*flip/i.test(n)) labels.add("Lip Flip");
    else if (/gummy\s*smile/i.test(n)) labels.add("Gummy Smile");
    else if (/brow\s*lift|browlift/i.test(n)) labels.add("Browlift");
    else if (/masseter/i.test(n)) labels.add("Masseters");
    else if (/trapezius/i.test(n)) labels.add("Trapezius");
    else labels.add(n);
  }
  const priority = [
    "Botox",
    "Dysport",
    "Daxxify",
    "Baby Tox",
    "Lip Flip",
    "Gummy Smile",
    "Browlift",
    "Masseters",
    "Trapezius",
  ];
  const rest = [...labels].filter((x) => !priority.includes(x));
  rest.sort((a, b) => a.localeCompare(b));
  return [...priority.filter((p) => labels.has(p)), ...rest];
}

/** Filter items within a price-list section to only those belonging to the dashboard category. */
function filterSkusForDashboardCategory(
  dash: DashboardTreatmentCategory,
  items: TreatmentPriceItem[],
  sheetSection: string,
): TreatmentPriceItem[] {
  switch (dash) {
    case "Neurotoxin":
      return items.filter(injectableMatchesNeurotoxinSku);
    case "Filler":
      return items.filter(injectableMatchesFillerSku);
    case "Biostimulants":
      return items.filter(injectableMatchesBiostimulantSku);
    case "Other procedures": {
      if (sheetSection === "Medical Spa") {
        return items.filter(
          (i) =>
            /PRFM\s*Injections/i.test(i.name) ||
            /PRFM\s*Hair\s*Restoration/i.test(i.name),
        );
      }
      if (sheetSection === "Injectables") {
        return items.filter(
          (i) =>
            (i.name.trim() === "Skinvive" && !/intradermal/i.test(i.name)) ||
            /spider\s*vein/i.test(i.name),
        );
      }
      if (sheetSection === "Other Treatments") {
        return items.filter((i) => !/dissolver/i.test(i.name));
      }
      return [];
    }
    case "Microneedling":
      return items.filter((i) => {
        const n = i.name;
        if (/PRFM\s*Injections/i.test(n) || /PRFM\s*Hair\s*Restoration/i.test(n)) return false;
        return i.name.includes("Microneedling") || i.name.includes("PRFM");
      });
    default:
      return items;
  }
}
